import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Network, Calendar, Wallet, BadgeCheck, AlertTriangle, RefreshCw, LifeBuoy, Download, FileText, XCircle, Link2, Award } from 'lucide-react';
import { toast } from 'sonner';
import { uploadImage } from '@/lib/upload';
import { startRazorpayCheckout, preloadRazorpay } from '@/lib/razorpay';
import { startCashfreeCheckout, preloadCashfree } from '@/lib/cashfree';
import { downloadBlob } from '@/lib/download';

const WalletsCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/network/me/wallets');
        if (alive) setData(res.data);
      } catch (err) {
        console.warn('wallets load failed', err);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);
  if (loading || !data) return null;
  const tds = data.tds || { effective_pct: 5, base_pct: 5, no_pan_pct: 10, pan_complete: true };
  return (
    <div className="mb-6">
    <div className="grid sm:grid-cols-2 gap-3" data-testid="wallets-row">
      <Card className="p-4 border-2 border-amber-400 bg-amber-50">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Hold Wallet</div>
          <span className="text-[10px] bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full font-bold">RELEASES SAT</span>
        </div>
        <div className="text-3xl font-black display text-amber-800" data-testid="wallet-hold-balance">₹{data.hold.balance}</div>
        <div className="text-xs text-amber-700 mt-1">{data.hold.entries} pending {data.hold.entries === 1 ? 'credit' : 'credits'} this cycle</div>
        <div className="text-[10px] text-[var(--bz-muted)] mt-2">Auto-releases every Saturday 03:00 UTC → moves to Main Wallet (after {tds.effective_pct}% TDS).</div>
      </Card>
      <Card className="p-4 border-2 border-blue-400 bg-blue-50">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] uppercase tracking-wider text-blue-700 font-bold">Main Wallet</div>
          <span className="text-[10px] bg-blue-200 text-blue-900 px-2 py-0.5 rounded-full font-bold">WITHDRAWABLE</span>
        </div>
        <div className="text-3xl font-black display text-blue-800" data-testid="wallet-main-balance">₹{data.main.balance}</div>
        <div className="text-xs text-blue-700 mt-1">Lifetime net ₹{data.main.lifetime_net} · TDS ₹{data.main.lifetime_tds} · Withdrawn ₹{data.main.withdrawn}</div>
        <div className="text-[10px] text-[var(--bz-muted)] mt-2">Your withdrawal is paid automatically every week to your registered bank account (once eKYC is verified).</div>
      </Card>
    </div>
    {tds.pan_complete ? (
      <div className="mt-2 flex items-center gap-2 text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-3 py-2" data-testid="tds-rate-note">
        <BadgeCheck className="h-4 w-4 shrink-0" />
        <span>PAN &amp; eKYC verified — lowest TDS rate of <b>{tds.effective_pct}%</b> applies to your payouts.</span>
      </div>
    ) : (tds.ekyc_status === 'submitted') ? (
      <div className="mt-2 flex items-center gap-2 text-[11px] text-blue-800 bg-blue-50 border border-blue-300 rounded-md px-3 py-2" data-testid="tds-rate-note">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span>Your eKYC is <b>under admin review</b>. Until it&apos;s verified the higher <b>{tds.no_pan_pct}% TDS</b> applies; once approved you&apos;ll drop to <b>{tds.base_pct}%</b>.</span>
      </div>
    ) : (
      <div className="mt-2 flex items-center gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded-md px-3 py-2" data-testid="tds-rate-note">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span>{tds.ekyc_status === 'rejected' ? 'Your eKYC was rejected — please recheck and resubmit. ' : ''}You&apos;re being charged the higher <b>{tds.no_pan_pct}% TDS</b> because your PAN / eKYC is not verified. Tap the <b>eKYC</b> button above to submit your PAN &amp; bank details and drop to just <b>{tds.base_pct}%</b> after admin approval.</span>
      </div>
    )}
    </div>
  );
};

const PurchaseInvoices = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/payments/me/purchases');
        if (alive) setRows(res.data.purchases || []);
      } catch (err) { if (alive) setRows([]); console.warn('purchases load failed', err); }
    })();
    return () => { alive = false; };
  }, []);

  const downloadInvoice = async (orderId) => {
    try {
      await downloadBlob({ path: `/payments/invoice/${orderId}`, method: 'GET', filename: `invoice-${orderId.slice(-8)}.pdf` });
    } catch (e) { toast.error(e.message || 'Download failed'); }
  };

  if (rows === null) return null;
  return (
    <Card className="p-4" data-testid="purchase-invoices">
      <div className="font-bold display mb-1 flex items-center gap-2"><FileText className="h-4 w-4 text-[#1D4ED8]" /> Website purchase invoices</div>
      <p className="text-xs text-[var(--bz-muted)] mb-3">GST tax invoices for your membership / website purchases. Tap to download a PDF.</p>
      {rows.length === 0 ? (
        <div className="text-sm text-[var(--bz-muted)] py-4 text-center" data-testid="invoices-empty">No paid purchases yet.</div>
      ) : (
        <div className="space-y-2">
          {rows.map(r => (
            <div key={r.rzp_order_id} className="flex items-center justify-between gap-3 border rounded-md px-3 py-2" data-testid={`invoice-row-${r.rzp_order_id}`}>
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">{r.label}</div>
                <div className="text-[11px] text-[var(--bz-muted)]">{(r.date || '').slice(0, 10)} · ₹{r.amount_inr} (incl. {r.gst_pct}% GST)</div>
              </div>
              <Button size="sm" variant="outline" className="gap-1 shrink-0" onClick={() => downloadInvoice(r.rzp_order_id)} data-testid={`download-invoice-${r.rzp_order_id}`}>
                <Download className="h-3.5 w-3.5" /> Invoice
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

const WeeklyStatements = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/network/me/weekly-statements?weeks=12');
        if (alive) setData(res.data);
      } catch (err) {
        if (alive) toast.error('Could not load statements');
        console.error(err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const downloadReport = async () => {
    setDownloading(true);
    try {
      await downloadBlob({ path: '/network/me/payout-report', method: 'GET', params: { weeks: 52 }, filename: 'indivalife-income-report.pdf' });
      toast.success('Income report downloaded');
    } catch (e) { toast.error(e.message || 'Download failed'); }
    finally { setDownloading(false); }
  };

  const hasData = data && data.weekly && data.weekly.length > 0;
  const totals = data?.totals || {};
  return (
    <div className="space-y-4" data-testid="weekly-statements">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="font-bold display text-lg">Income & payouts</div>
        <Button variant="outline" className="gap-2" onClick={downloadReport} disabled={downloading} data-testid="download-payout-report-btn">
          <Download className="h-4 w-4" /> {downloading ? 'Preparing…' : 'Download report (PDF)'}
        </Button>
      </div>
      {loading && <Card className="p-6 text-center text-[var(--bz-muted)]">Loading…</Card>}
      {!loading && !hasData && (
        <Card className="p-6 text-center text-[var(--bz-muted)]" data-testid="weekly-statements-empty">No payouts yet — your first weekly statement appears here after Saturday cycle.</Card>
      )}
      {!loading && hasData && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)]">Total gross (12 wks)</div><div className="text-xl font-black display">₹{totals.gross}</div></Card>
            <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)]">Total TDS</div><div className="text-xl font-black display text-amber-700">₹{totals.tds}</div></Card>
            <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)]">Net paid out</div><div className="text-xl font-black display text-[#1D4ED8]">₹{totals.net}</div></Card>
            <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)]">Forfeited</div><div className="text-xl font-black display text-red-600">₹{totals.forfeited}</div></Card>
          </div>
          <Card className="p-0 overflow-x-auto bz-scroll">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-[var(--bz-soft)]">
                <tr>
                  <th className="text-left p-3">Week</th>
                  <th className="text-right p-3">Gross</th>
                  <th className="text-right p-3">TDS</th>
                  <th className="text-right p-3">Net payout</th>
                  <th className="text-right p-3">Forfeited</th>
                </tr>
              </thead>
              <tbody>
                {data.weekly.map(w => (
                  <tr key={w.week_key} className="border-t" data-testid={`statement-row-${w.week_key}`}>
                    <td className="p-3 font-mono-bz text-xs">{w.week_key}</td>
                    <td className="p-3 text-right font-mono-bz font-bold">₹{w.gross}</td>
                    <td className="p-3 text-right font-mono-bz text-amber-700">−₹{w.tds}</td>
                    <td className="p-3 text-right font-mono-bz font-bold text-[#1D4ED8]">₹{w.net}</td>
                    <td className="p-3 text-right font-mono-bz text-red-600">{w.forfeited > 0 ? `₹${w.forfeited}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
      <PurchaseInvoices />
    </div>
  );
};

// Round, level-by-level genealogy node. Click → drill into that downline's subtree.
const TreeNodeCircle = ({ node, onDrill, isRoot = false }) => {
  if (!node) {
    return (
      <div className="flex flex-col items-center opacity-50" data-testid="tree-empty">
        <div className="w-16 h-16 rounded-full border-2 border-dashed border-[var(--bz-muted)] flex items-center justify-center bg-white">
          <span className="text-[10px] text-[var(--bz-muted)]">Empty</span>
        </div>
        <div className="text-[10px] text-[var(--bz-muted)] mt-1 italic">leg</div>
      </div>
    );
  }
  const isActive = node.active !== false;
  const ringColor = isRoot ? 'border-[#1E3A8A] ring-2 ring-[#1E3A8A]/30' : isActive ? 'border-blue-500' : 'border-red-400';
  const bgColor = isRoot ? 'bg-[#1E3A8A] text-white' : isActive ? 'bg-blue-50' : 'bg-red-50';
  const initials = (node.full_name || node.personal_id || '?').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();
  const clickable = !isRoot && onDrill;
  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={clickable ? () => onDrill(node.personal_id) : undefined}
      data-testid={`tree-node-${node.personal_id}`}
      className={`group flex flex-col items-center transition-transform ${clickable ? 'cursor-pointer hover:scale-105 active:scale-95' : 'cursor-default'}`}
      title={clickable ? `Click to view ${node.personal_id}'s downline` : undefined}
    >
      <div className={`relative w-20 h-20 rounded-full border-2 ${ringColor} ${bgColor} flex flex-col items-center justify-center shadow-sm ${clickable ? 'group-hover:shadow-md' : ''}`}>
        <span className="text-lg font-black display leading-none">{initials}</span>
        <span className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${isActive ? 'bg-blue-500' : 'bg-red-500'}`} title={isActive ? 'Active' : 'Inactive'} />
      </div>
      <div className="text-[11px] font-mono-bz font-bold mt-1 text-[var(--bz-ink)]">{node.personal_id}</div>
      <div className="text-[10px] truncate max-w-[88px] text-[var(--bz-muted)]">{node.full_name || ''}</div>
      <div className="text-[9px] text-[var(--bz-muted)]">L:{node.left_bv ?? 0} · R:{node.right_bv ?? 0}</div>
    </button>
  );
};

// Recursive subtree rendering — root at top, two legs underneath, levels stack
// vertically with thin connector lines.
// NOTE: we deliberately use React.createElement for the self-recursive calls
// instead of JSX `<SubTree .../>` — the @emergentbase/visual-edits Babel
// plugin's JSXOpeningElement visitor infinite-loops on self-referencing JSX
// components and crashes the dev-server compile with a RangeError.
const SubTree = ({ node, depth = 0, maxDepth = 4, onDrill }) => {
  if (!node || depth >= maxDepth) return null;
  const hasChildren = !!(node.left || node.right);
  return (
    <div className="flex flex-col items-center">
      <TreeNodeCircle node={node} onDrill={onDrill} isRoot={depth === 0} />
      {hasChildren && (
        <>
          <div className="w-px h-4 bg-[#1E3A8A]/30 mt-1" />
          <div className="flex gap-6 sm:gap-10 lg:gap-14 items-start relative">
            <div className="absolute top-0 left-1/4 right-1/4 h-px bg-[#1E3A8A]/30" />
            <div className="pt-4">
              {React.createElement(SubTree, { node: node.left, depth: depth + 1, maxDepth, onDrill })}
            </div>
            <div className="pt-4">
              {React.createElement(SubTree, { node: node.right, depth: depth + 1, maxDepth, onDrill })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const TreeView = ({ root, onDrill }) => {
  if (!root) return <div className="text-sm text-[var(--bz-muted)]">No tree</div>;
  return (
    <div className="flex justify-center min-w-fit py-4">
      <SubTree node={root} onDrill={onDrill} />
    </div>
  );
};

const NetworkPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tree, setTree] = useState(null);
  const [signupOpen, setSignupOpen] = useState(false);
  const [renewOpen, setRenewOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [ekycOpen, setEkycOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [settings, setSettings] = useState({ membership_fee: 600, membership_gst_pct: 18, renewal_fee: 708 });
  // Selected membership tier — drives which fee/BV/validity is used at checkout.
  //  'membership'      → legacy 90-day tier (₹600+GST, BV=7)
  //  'website_yearly'  → 1-year tier        (₹600+GST, BV=7)
  //  'website_monthly' → 30-day tier        (₹84+GST,  BV=1)
  const JOIN_FORM_KEY = 'il_join_form_v1';
  const _readSavedJoin = () => {
    try {
      const raw = localStorage.getItem(JOIN_FORM_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && saved.ts && (Date.now() - saved.ts) < 2 * 60 * 60 * 1000) return saved;
      }
    } catch { /* ignore */ }
    return null;
  };
  const _savedJoin = _readSavedJoin();
  const [selectedTier, setSelectedTier] = useState((_savedJoin && _savedJoin.tier) || 'membership');
  const _signupDefaults = {
    sponsor_id: '', leg: 'left', parental_id: '', full_name: '', first_name: '', last_name: '', phone: '',
    address: '', city: '', state: '', pincode: '',
    dob: '', nominee_name: '', nominee_relationship: '',
    aadhar_number: '', pan_number: '', bank_account: '', ifsc: '',
    aadhar_image_url: '', aadhar_back_image_url: '', pan_image_url: '', bankbook_image_url: '',
    password: '', confirm_password: '',
    payment_screenshot_url: '', utr: '', payment_amount: 708,
  };
  const [signupForm, setSignupForm] = useState(
    _savedJoin && _savedJoin.form ? { ..._signupDefaults, ..._savedJoin.form } : _signupDefaults
  );
  const [renewForm, setRenewForm] = useState({ utr: '', amount: 708, payment_screenshot_url: '', renewal_type: 'self' });
  const [ekyc, setEkyc] = useState({ aadhar_number: '', pan_number: '', bank_account: '', ifsc: '', aadhar_image_url: '', aadhar_back_image_url: '', pan_image_url: '', bankbook_image_url: '' });
  const [ekycStatus, setEkycStatus] = useState('none');
  const [withdrawAmt, setWithdrawAmt] = useState(0);

  // eslint-disable-next-line no-unused-vars
  const [uploading, setUploading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    purpose: 'membership', leg: 'left', parental_id: '',
    first_name: '', last_name: '', phone: '',
    dob: '', nominee_name: '', nominee_relationship: '',
    address: '', city: '', state: '', pincode: '',
    aadhar_number: '', pan_number: '', bank_account: '', ifsc: '',
    aadhar_image_url: '', aadhar_back_image_url: '', pan_image_url: '', bankbook_image_url: '',
    password: '', confirm_password: '',
  });
  const [dlCreds, setDlCreds] = useState(null);
  const [joinProvider, setJoinProvider] = useState('razorpay');
  const [dlProvider, setDlProvider] = useState('razorpay');
  const [kits, setKits] = useState([]);
  const [selectedKitId, setSelectedKitId] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [sponsorInfo, setSponsorInfo] = useState(null);
  const [parentalInfo, setParentalInfo] = useState(null);
  const [addParentalInfo, setAddParentalInfo] = useState(null);

  const lookupId = async (id, setter) => {
    if (!id || id.length < 3) { setter(null); return; }
    try { const { data } = await api.get(`/network/lookup/${id.toUpperCase()}`); setter(data); }
    catch { setter({ error: 'not_found' }); }
  };

  // Per-tier pricing derived from settings — keeps the UI in lock-step with
  // admin's Pricing tab. Returns { fee, gst, total, days, bv, refIncome, label }.
  const tierConfig = (tier, kitId) => {
    if (tier === 'entry_kit') {
      const kid = kitId || selectedKitId;
      const kit = kits.find(k => k.kit_id === kid) || kits[0];
      if (kit) {
        return {
          key: 'entry_kit', label: 'Purchase Kit', kit_id: kit.kit_id, name: kit.name, image: kit.image_url,
          fee: kit.cost, gstPct: kit.gst_pct, gst: +Number(kit.gst || 0).toFixed(2), delivery: kit.delivery,
          total: Math.round(kit.total), days: kit.validity_days, bv: kit.bv, refIncome: kit.referral_income,
        };
      }
    }
    const key = {
      membership: { fee: 'membership_fee', gst: 'membership_gst_pct', bv: 'membership_bv', days: 'membership_validity_days', ref: 'referral_income_90d', label: '90-day' },
      website_yearly: { fee: 'website_yearly_fee', gst: 'website_yearly_gst_pct', bv: 'website_yearly_bv', days: 'website_yearly_validity_days', ref: 'referral_income_yearly', label: 'Yearly' },
      website_monthly: { fee: 'website_monthly_fee', gst: 'website_monthly_gst_pct', bv: 'website_monthly_bv', days: 'website_monthly_validity_days', ref: 'referral_income_monthly', label: 'Monthly' },
      entry_kit: { fee: 'entry_kit_cost', gst: 'entry_kit_gst_pct', bv: 'entry_kit_bv', days: 'entry_kit_validity_days', ref: 'referral_income_entry_kit', label: 'Purchase Kit' },
    }[tier] || {};
    const num = (v, d) => { const n = Number(v); return Number.isFinite(n) ? n : d; };
    const fee = num(settings[key.fee], tier === 'website_monthly' ? 84 : 600);
    const gstPct = num(settings[key.gst], 18);
    const gst = +(fee * gstPct / 100).toFixed(2);
    const delivery = tier === 'entry_kit' ? num(settings.entry_kit_delivery, 60) : 0;
    const total = Math.round(fee + gst + delivery);
    return {
      key: tier,
      label: key.label,
      fee, gstPct, gst, delivery, total,
      days: num(settings[key.days], tier === 'website_monthly' || tier === 'entry_kit' ? 30 : tier === 'website_yearly' ? 365 : 90),
      bv: num(settings[key.bv], tier === 'website_monthly' ? 1 : 7),
      refIncome: num(settings[key.ref], num(settings.referral_income, 100)),
    };
  };
  const currentTier = tierConfig(selectedTier);

  // Genealogy drill-down + ID search state
  const [treeStack, setTreeStack] = useState([]);   // breadcrumb of pids we've drilled into
  const [searchId, setSearchId] = useState('');
  const [searchResult, setSearchResult] = useState(null);   // { personal_id, full_name, active, ... } | { error }
  const [searchBusy, setSearchBusy] = useState(false);

  const drillIntoNode = async (pid) => {
    try {
      const { data } = await api.get(`/network/tree?root=${encodeURIComponent(pid)}`);
      setTree(data);
      setTreeStack(s => [...s, pid]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load that subtree');
    }
  };
  const drillUp = async () => {
    const newStack = treeStack.slice(0, -1);
    setTreeStack(newStack);
    const root = newStack[newStack.length - 1];
    const { data } = await api.get(root ? `/network/tree?root=${encodeURIComponent(root)}` : '/network/tree');
    setTree(data);
  };
  const drillReset = async () => {
    setTreeStack([]);
    const { data } = await api.get('/network/tree');
    setTree(data);
  };

  const runSearch = async () => {
    const id = searchId.trim().toUpperCase();
    if (!id) { setSearchResult(null); return; }
    setSearchBusy(true);
    try {
      const { data } = await api.get(`/network/lookup/${encodeURIComponent(id)}`);
      setSearchResult(data);
    } catch (e) {
      setSearchResult({ error: e.response?.data?.detail || 'not_found', personal_id: id });
    } finally { setSearchBusy(false); }
  };

  const loadAll = async () => {
    const { data } = await api.get('/network/me'); setMe(data);
    if (data.node) {
      const t = await api.get('/network/tree'); setTree(t.data);
    }
    const s = await api.get('/settings/public'); setSettings(s.data);
    const e = await api.get('/network/ekyc'); if (e.data && e.data.aadhar_number) setEkyc(e.data); setEkycStatus(e.data?.status || 'none');
    setRenewForm(f => ({ ...f, amount: s.data.renewal_fee || 708 }));
    try {
      const k = await api.get('/network/purchase-kits');
      const list = k.data?.kits || [];
      setKits(list);
      setSelectedKitId(prev => prev || (list[0]?.kit_id || null));
    } catch { /* ignore */ }
  };
  useEffect(() => { loadAll(); }, []);

  // Preload the Razorpay SDK so the checkout opens instantly on tap (mobile fix).
  useEffect(() => { preloadRazorpay(); preloadCashfree(); }, []);

  // Finish a Cashfree payment after the hosted-checkout full-page redirect returns.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cfOrder = params.get('cf_verify');
    if (!cfOrder) return;
    let ctx = {};
    try { ctx = JSON.parse(localStorage.getItem('il_cf_ctx') || '{}'); } catch { ctx = {}; }
    // Clean the URL so a refresh doesn't re-trigger.
    window.history.replaceState({}, '', '/network');
    (async () => {
      try {
        const { data } = await api.post('/payments/cashfree/verify', { order_id: cfOrder });
        if (data.status === 'paid') {
          localStorage.removeItem('il_cf_ctx');
          clearJoinForm();
          if (ctx.isDownline) {
            setDlCreds({ personal_id: data.personal_id, password: ctx.password, name: ctx.name });
          } else {
            toast.success(`Payment successful — your ID: ${data.personal_id || ''}`);
          }
          loadAll();
        } else {
          toast.error(`Payment not completed (status: ${data.status}). If money was debited it will auto-refund.`);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not confirm the Cashfree payment');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Entry-Kit share link: /network?sponsor=<pid>&tier=entry_kit opens the join
  // dialog with the sponsor prefilled so an invited prospect can join & pay.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sp = params.get('sponsor');
    const tier = params.get('tier');
    const kit = params.get('kit');
    if (!sp) return;
    setSignupForm(f => ({ ...f, sponsor_id: sp.toUpperCase() }));
    if (['entry_kit', 'membership', 'website_yearly'].includes(tier)) setSelectedTier(tier);
    if (kit) setSelectedKitId(kit);
    setSignupOpen(true);
    const url = new URL(window.location.href);
    url.searchParams.delete('sponsor'); url.searchParams.delete('tier'); url.searchParams.delete('kit');
    window.history.replaceState({}, '', url.pathname + url.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shareEntryKitLink = async (kitId) => {
    const pid = me?.node?.personal_id;
    if (!pid) { toast.error('Your member ID is not ready yet'); return; }
    const kit = kits.find(k => k.kit_id === kitId);
    const kitParam = kitId ? `&kit=${kitId}` : '';
    const url = `${window.location.origin}/network?sponsor=${pid}&tier=entry_kit${kitParam}`;
    const text = `Join INDIVALIFE with ${kit ? `the "${kit.name}" kit` : 'a Purchase Kit'} & activate your ID 👇\n${url}`;
    try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
    if (navigator.share) {
      try { await navigator.share({ title: 'Join INDIVALIFE', text, url }); return; } catch { /* fall through */ }
    }
    toast.success('Purchase Kit link copied — share it on WhatsApp!');
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };


  // Persist the join / eKYC form so stepping out of the page (to copy Aadhaar/PAN
  // numbers, pick files, or when the mobile browser backgrounds & reloads the tab)
  // never wipes what the user already typed & uploaded. Initial state is hydrated
  // lazily from localStorage above; here we keep it in sync. Cleared once the join
  // succeeds; entries older than 2 hours are ignored on read.
  const clearJoinForm = () => { try { localStorage.removeItem(JOIN_FORM_KEY); } catch { /* ignore */ } };
  useEffect(() => {
    try {
      localStorage.setItem(JOIN_FORM_KEY, JSON.stringify({ form: signupForm, tier: selectedTier, ts: Date.now() }));
    } catch { /* ignore */ }
  }, [signupForm, selectedTier]);


  // Auto-refetch settings when the user comes back to the tab / window OR
  // every 60 s while open — so admin edits propagate to the user dashboard
  // without a manual reload (fixes the "stale pricing / referral income" bug).
  useEffect(() => {
    const refreshSettings = async () => {
      try {
        const s = await api.get('/settings/public');
        setSettings(s.data);
      } catch (err) { console.warn('settings refresh failed', err); }
    };
    const onFocus = () => { refreshSettings(); };
    window.addEventListener('focus', onFocus);
    const tid = setInterval(refreshSettings, 60_000);
    return () => { window.removeEventListener('focus', onFocus); clearInterval(tid); };
  }, []);

  // Keep signupForm.payment_amount in sync with the selected tier so the
  // manual-UPI flow shows the right total.
  useEffect(() => {
    setSignupForm(f => ({ ...f, payment_amount: currentTier.total, purpose: selectedTier }));
  }, [selectedTier, settings]);

  const uploadFile = async (e, setter, key) => {
    const file = e.target.files?.[0]; if (!file) return;
    const res = await uploadImage(file);
    if (res) setter(prev => ({ ...prev, [key]: res.url }));
    e.target.value = '';
  };

  const [rzpBusy, setRzpBusy] = useState(false);

  const payWithRazorpay = async (purpose) => {
    // purpose defaults to whatever tier the user clicked. Membership = legacy 90-day.
    purpose = purpose || selectedTier || 'membership';
    if (['membership', 'website_yearly', 'website_monthly', 'entry_kit'].includes(purpose)) {
      // sponsor_id is auto-defaulted on the server to INDIVALIFE if blank.
      if (!signupForm.first_name || !signupForm.last_name) { toast.error('First name & last name are required'); return; }
      if (!signupForm.phone) { toast.error('Phone required'); return; }
      // 18+ DOB check
      if (!signupForm.dob) { toast.error('Date of birth is required'); return; }
      const age = Math.floor((Date.now() - new Date(signupForm.dob).getTime()) / (365.25 * 24 * 3600 * 1000));
      if (isNaN(age) || age < 18) { toast.error('You must be at least 18 years old to join the network'); return; }
      if (!signupForm.nominee_name || !signupForm.nominee_relationship) { toast.error('Nominee name & relationship are required'); return; }
      // Full address is mandatory for every network entry
      if (!signupForm.address || !signupForm.city || !signupForm.state || !signupForm.pincode) {
        toast.error('Full address (address, city, state, pincode) is required');
        return;
      }
      // Full eKYC MUST be completed before payment
      if (!signupForm.aadhar_number || !signupForm.pan_number || !signupForm.bank_account || !signupForm.ifsc) {
        toast.error('Complete your eKYC — Aadhaar, PAN, bank account & IFSC are required before payment');
        return;
      }
      if (!signupForm.aadhar_image_url || !signupForm.aadhar_back_image_url || !signupForm.pan_image_url || !signupForm.bankbook_image_url) {
        toast.error('Upload all eKYC documents (Aadhaar front & back, PAN, bank book) before payment');
        return;
      }
      // Password is created here (during KYC) so the member can log in with ID + password.
      const pw = signupForm.password || '';
      if (pw.length < 8 || !/[a-zA-Z]/.test(pw) || !/\d/.test(pw)) {
        toast.error('Create a login password: 8+ characters with at least one letter and one number');
        return;
      }
      if (pw !== signupForm.confirm_password) { toast.error('Passwords do not match'); return; }
    }
    // Entry Kit ships a physical kit — address is mandatory.
    if (purpose === 'entry_kit') {
      if (!signupForm.address || !signupForm.pincode) {
        toast.error('Please enter your full delivery address and pincode for the kit');
        return;
      }
    }
    // For renewals (no signupForm), fall back to authenticated user's saved phone.
    const fallbackPhone = signupForm.phone || user?.phone;
    const fullName = signupForm.full_name || `${signupForm.first_name || ''} ${signupForm.last_name || ''}`.trim() || user?.name;
    setRzpBusy(true);
    try {
      const orderPayload = {
        purpose,
        sponsor_id: signupForm.sponsor_id,
        leg: signupForm.leg,
        parental_id: signupForm.parental_id,
        full_name: fullName,
        first_name: signupForm.first_name,
        last_name: signupForm.last_name,
        phone: fallbackPhone,
        address: signupForm.address,
        city: signupForm.city,
        state: signupForm.state,
        pincode: signupForm.pincode,
        dob: signupForm.dob,
        nominee_name: signupForm.nominee_name,
        nominee_relationship: signupForm.nominee_relationship,
        aadhar_number: signupForm.aadhar_number,
        pan_number: signupForm.pan_number,
        bank_account: signupForm.bank_account,
        ifsc: signupForm.ifsc,
        aadhar_image_url: signupForm.aadhar_image_url,
        aadhar_back_image_url: signupForm.aadhar_back_image_url,
        pan_image_url: signupForm.pan_image_url,
        bankbook_image_url: signupForm.bankbook_image_url,
        password: signupForm.password,
        ...(purpose === 'entry_kit' ? { kit_id: selectedKitId } : {}),
      };
      if (joinProvider === 'cashfree') {
        // Cashfree redirects the whole page — stash context so we can finish on return.
        localStorage.setItem('il_cf_ctx', JSON.stringify({ isDownline: false, purpose }));
        await startCashfreeCheckout({ createPath: '/payments/razorpay/create-order', orderPayload });
        return; // browser navigates to Cashfree hosted checkout
      }
      const result = await startRazorpayCheckout({
        purpose,
        sponsor_id: signupForm.sponsor_id,
        leg: signupForm.leg,
        parental_id: signupForm.parental_id,
        full_name: fullName,
        phone: fallbackPhone,
        orderPayload,
        prefill: { name: fullName, email: user?.email, contact: fallbackPhone },
        onDismiss: () => toast('Payment cancelled'),
      });
      if (result.dismissed) return;
      if (result.status === 'paid' || result.status === 'already_paid') {
        toast.success(`${purpose === 'entry_kit' ? 'Purchase Kit bought' : 'Website Purchase active'} — your ID: ${result.personal_id}`);
        setSignupOpen(false);
        setRenewOpen(false);
        clearJoinForm();
        loadAll();
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.description || e?.message || 'Payment failed';
      toast.error(msg, {
        action: msg.includes('mobile number') ? { label: 'Update Profile', onClick: () => navigate('/profile') } : undefined,
      });
    } finally {
      setRzpBusy(false);
    }
  };

  const submitEKYC = async () => {
    try {
      await api.post('/network/ekyc', ekyc); toast.success('eKYC submitted'); setEkycOpen(false); loadAll();
    } catch { toast.error('Failed'); }
  };

  const [addBusy, setAddBusy] = useState(false);
  const payForDownline = async () => {
    const f = addForm;
    if (!f.first_name || !f.last_name) { toast.error('First & last name required'); return; }
    if (!f.phone) { toast.error('Phone required'); return; }
    if (!f.dob) { toast.error('Date of birth required'); return; }
    const age = Math.floor((Date.now() - new Date(f.dob).getTime()) / (365.25 * 24 * 3600 * 1000));
    if (isNaN(age) || age < 18) { toast.error('Downline must be at least 18 years old'); return; }
    if (!f.nominee_name || !f.nominee_relationship) { toast.error('Nominee name & relationship required'); return; }
    if (!f.address || !f.city || !f.state || !f.pincode) { toast.error('Full address (address, city, state, pincode) required'); return; }
    if (!f.aadhar_number || !f.pan_number || !f.bank_account || !f.ifsc) { toast.error('Complete the downline eKYC — Aadhaar, PAN, bank & IFSC required'); return; }
    if (!f.aadhar_image_url || !f.aadhar_back_image_url || !f.pan_image_url || !f.bankbook_image_url) { toast.error('Upload all eKYC documents (Aadhaar front & back, PAN, bank book) before payment'); return; }
    const pw = f.password || '';
    if (pw.length < 8 || !/[a-zA-Z]/.test(pw) || !/\d/.test(pw)) { toast.error('Set a login password for the member: 8+ chars with a letter and a number'); return; }
    if (pw !== f.confirm_password) { toast.error('Passwords do not match'); return; }
    if (!user?.phone) { toast.error('Add your own 10-digit mobile number in Profile first — Razorpay needs it to open.', { action: { label: 'Update Profile', onClick: () => navigate('/profile') } }); return; }
    setAddBusy(true);
    try {
      if (dlProvider === 'cashfree') {
        localStorage.setItem('il_cf_ctx', JSON.stringify({ isDownline: true, password: f.password, name: `${f.first_name} ${f.last_name}`.trim() }));
        await startCashfreeCheckout({ createPath: '/network/downline/create-order', orderPayload: { ...f } });
        return; // browser navigates to Cashfree hosted checkout
      }
      const result = await startRazorpayCheckout({
        createPath: '/network/downline/create-order',
        orderPayload: { ...f },
        prefill: { name: user?.name, email: user?.email, contact: user?.phone },
        onDismiss: () => toast('Payment cancelled'),
      });
      if (result.dismissed) return;
      if (result.status === 'paid' || result.status === 'already_paid') {
        // Surface the new member's ID + password so the sponsor can hand it over on the spot.
        setDlCreds({ personal_id: result.personal_id, password: f.password, name: `${f.first_name} ${f.last_name}`.trim() });
        toast.success(`Downline added — new ID: ${result.personal_id}`);
        setAddOpen(false);
        setAddForm({ purpose: 'membership', leg: 'left', parental_id: '', first_name: '', last_name: '', phone: '', dob: '', nominee_name: '', nominee_relationship: '', address: '', city: '', state: '', pincode: '', aadhar_number: '', pan_number: '', bank_account: '', ifsc: '', aadhar_image_url: '', aadhar_back_image_url: '', pan_image_url: '', bankbook_image_url: '', password: '', confirm_password: '' });
        loadAll();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Payment failed');
    } finally { setAddBusy(false); }
  };

  const submitSetPassword = async () => {
    if (pwForm.new_password.length < 8) { toast.error('Password must be 8+ characters with letters & digits'); return; }
    if (pwForm.new_password !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    try {
      const body = { new_password: pwForm.new_password };
      if (pwForm.current_password) body.current_password = pwForm.current_password;
      await api.post('/auth/password/set', body);
      toast.success('Password updated — you can now login with your Personal ID');
      setPwOpen(false);
      setPwForm({ current_password: '', new_password: '', confirm: '' });
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg.replace(/_/g, ' ') : 'Failed');
    }
  };

  // No node yet -> show signup CTA
  if (!me) return <div className="p-6 text-sm text-[var(--bz-muted)]">Loading network…</div>;

  if (!me.node) {
    return (
      <div className="p-6 max-w-screen-xl mx-auto">
        <div className="bz-card p-8 max-w-2xl">
          <div className="flex items-center gap-2 mb-3">
            <Network className="h-8 w-8 text-[#1E3A8A]" />
            <h1 className="text-3xl font-black tracking-tighter display">Network Marketing</h1>
          </div>
          <p className="text-[var(--bz-muted)] mb-4 leading-relaxed">
            Join the INDIVALIFE Network — build your own binary business, grow your team, and get paid every week.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 mb-6">
            <div className="bz-card p-3"><div className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)]">Membership</div><div className="text-xl font-black display">₹{settings.membership_fee || 600} + {settings.membership_gst_pct || 18}% GST</div></div>
            <div className="bz-card p-3"><div className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)]">Validity</div><div className="text-xl font-black display">3 months</div></div>
          </div>
          {me.signup?.status === 'pending' ? (
            <Badge variant="outline" className="border-yellow-500 text-yellow-700">Awaiting admin approval — within 24 hours</Badge>
          ) : (
            <div className="flex gap-2">
              <Button onClick={() => setSignupOpen(true)} className="bg-[#1D4ED8] hover:bg-[#1E3A8A]" data-testid="open-signup-btn">Become a member</Button>
              <Button variant="outline" asChild data-testid="vmail-sponsor-link"><a href={`mailto:${settings.support_vmail || 'support@indivalife.com'}?subject=Need%20Sponsor%20ID`}><LifeBuoy className="h-4 w-4 mr-1" />Ask support for Sponsor ID</a></Button>
            </div>
          )}
        </div>

        <Dialog open={signupOpen} onOpenChange={setSignupOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bz-scroll">
            <DialogHeader><DialogTitle>Choose your membership plan</DialogTitle></DialogHeader>

            {/* Tier selector — pickable tiles. Each shows price, BV, validity, referral kickback. */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="tier-selector">
              {['entry_kit', 'membership', 'website_yearly'].map(t => {
                const c = tierConfig(t);
                const active = selectedTier === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setSelectedTier(t)}
                    data-testid={`tier-${t}`}
                    className={`text-left rounded-lg border-2 p-3 transition-all ${active ? 'border-[#1D4ED8] bg-[var(--bz-green-tint)] shadow-md' : 'border-gray-200 bg-white hover:border-[#1D4ED8]/50'}`}
                  >
                    <div className="text-[10px] uppercase tracking-wider font-bold text-[#1E3A8A]">{c.label}</div>
                    <div className="text-lg font-black display text-[#1E3A8A] mt-1">₹{c.total}</div>
                    <div className="text-[10px] text-[var(--bz-muted)]">incl. {c.gstPct}% GST{c.delivery ? ` + ₹${c.delivery} delivery` : ''}</div>
                    <div className="text-[10px] text-[var(--bz-muted)] mt-1">{c.days} days · BV {c.bv}</div>
                  </button>
                );
              })}
            </div>
            {selectedTier === 'entry_kit' && (
              <div className="rounded-lg bg-[#EAF1FF] p-2.5 text-[11px] text-[#1E3A8A] space-y-2" data-testid="purchase-kit-picker">
                <div className="font-bold text-xs">Choose a Purchase Kit</div>
                {kits.length === 0 && <div className="text-[var(--bz-muted)]">No kits available right now.</div>}
                <div className="grid grid-cols-1 gap-2">
                  {kits.map(k => {
                    const active = selectedKitId === k.kit_id;
                    return (
                      <button key={k.kit_id} type="button" onClick={() => setSelectedKitId(k.kit_id)} data-testid={`kit-${k.kit_id}`}
                        className={`flex gap-3 items-center text-left rounded-lg border-2 p-2 transition-all ${active ? 'border-[#1D4ED8] bg-white shadow-sm' : 'border-transparent bg-white/60 hover:border-[#1D4ED8]/40'}`}>
                        {k.image_url
                          ? <img src={k.image_url} alt={k.name} className="h-14 w-14 rounded-md object-cover shrink-0 border" />
                          : <div className="h-14 w-14 rounded-md bg-[#dbe6ff] grid place-items-center shrink-0">📦</div>}
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-[13px] text-[#1E3A8A] truncate">{k.name}</div>
                          <div className="text-[10px] text-[var(--bz-muted)]">{k.validity_days} days · BV {k.bv}{k.delivery ? ` · +₹${k.delivery} delivery` : ''}</div>
                          <div className="text-sm font-black display text-[#1E3A8A]">₹{Math.round(k.total)}</div>
                        </div>
                        {active && <div className="text-[#1D4ED8] font-bold text-lg shrink-0">✓</div>}
                      </button>
                    );
                  })}
                </div>
                <div>📦 A physical kit is delivered to your address. It activates your ID + personal website. Enter your delivery address below before paying.</div>
              </div>
            )}

            <div className="rounded-lg border-2 border-[#1D4ED8] bg-[var(--bz-green-tint)] p-3 space-y-2">
              <div className="text-[10px] uppercase tracking-wider text-[#1E3A8A] font-bold">Pricing breakdown — {currentTier.label}</div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span>Plan fee ({currentTier.days} days)</span><span>₹{currentTier.fee}</span></div>
                <div className="flex justify-between text-[var(--bz-muted)]"><span>GST ({currentTier.gstPct}%)</span><span>₹{currentTier.gst.toFixed(2)}</span></div>
                {currentTier.delivery > 0 && <div className="flex justify-between text-[var(--bz-muted)]"><span>Delivery</span><span>₹{currentTier.delivery}</span></div>}
                <div className="border-t border-[#1D4ED8]/30 pt-1 flex justify-between font-bold text-base text-[#1E3A8A]"><span>Total payable</span><span data-testid="membership-total">₹{currentTier.total}</span></div>
              </div>
              <div className="pt-1 space-y-2">
                <div className="text-sm font-semibold">Choose payment method</div>
                <div className="grid grid-cols-2 gap-2" data-testid="join-provider-selector">
                  {[['razorpay', 'Razorpay'], ['cashfree', 'Cashfree']].map(([val, label]) => (
                    <button key={val} type="button" onClick={() => setJoinProvider(val)} data-testid={`join-provider-${val}`}
                      className={`rounded-lg border-2 p-2 text-sm font-bold transition-all ${joinProvider === val ? 'border-[#1D4ED8] bg-[var(--bz-green-tint)] text-[#1E3A8A]' : 'border-gray-200 bg-white text-[var(--bz-muted)]'}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <div className="text-[11px] text-[var(--bz-muted)]">UPI · Cards · Net Banking · Wallets</div>
                <Button
                  onClick={() => payWithRazorpay(selectedTier)}
                  disabled={rzpBusy}
                  className="w-full bg-[#1D4ED8] hover:bg-[#1E3A8A] gap-2 h-11 px-5 font-bold display"
                  data-testid="rzp-pay-membership-btn"
                >
                  {rzpBusy ? 'Opening…' : `Pay ₹${currentTier.total} with ${joinProvider === 'cashfree' ? 'Cashfree' : 'Razorpay'}`}
                </Button>
              </div>
              <p className="text-[10px] text-[var(--bz-muted)]">Name & phone required. Sponsor ID is optional — leave blank to join under <b>INDIVALIFE</b> (company admin). Membership activates instantly — no admin wait.</p>
            </div>
            <div className="space-y-3">
              <div>
                <Label>Sponsor ID *</Label>
                <Input value={signupForm.sponsor_id} onChange={e => { setSignupForm({ ...signupForm, sponsor_id: e.target.value.toUpperCase() }); lookupId(e.target.value, setSponsorInfo); }} placeholder="e.g. IL10001 or INDIVALIFE" data-testid="sponsor-id-input" />
                {sponsorInfo && !sponsorInfo.error && <div className="text-xs text-[#1E3A8A] mt-1 font-semibold" data-testid="sponsor-name">✓ {sponsorInfo.full_name}{sponsorInfo.town ? ` · ${sponsorInfo.town}` : ''}</div>}
                {sponsorInfo?.error && <div className="text-xs text-red-600 mt-1">Sponsor ID not found</div>}
                <div className="text-[11px] text-[var(--bz-muted)] mt-1 flex items-center justify-between gap-2">
                  <span>💡 No referrer? Tap →</span>
                  <button
                    type="button"
                    onClick={() => { setSignupForm(f => ({ ...f, sponsor_id: 'INDIVALIFE' })); lookupId('INDIVALIFE', setSponsorInfo); }}
                    className="font-bold text-[#1E3A8A] underline hover:text-[#1D4ED8]"
                    data-testid="use-admin-sponsor-btn"
                  >
                    Use INDIVALIFE (company admin)
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Leg *</Label>
                  <Select value={signupForm.leg} onValueChange={(v) => setSignupForm({ ...signupForm, leg: v })}>
                    <SelectTrigger data-testid="leg-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="left">Left</SelectItem><SelectItem value="right">Right</SelectItem></SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Parental ID (optional)</Label>
                  <Input value={signupForm.parental_id} onChange={e => { setSignupForm({ ...signupForm, parental_id: e.target.value.toUpperCase() }); lookupId(e.target.value, setParentalInfo); }} />
                  {parentalInfo && !parentalInfo.error && <div className="text-xs text-[#1E3A8A] mt-1 font-semibold">✓ {parentalInfo.full_name}</div>}
                  {parentalInfo?.error && signupForm.parental_id && <div className="text-xs text-red-600 mt-1">Not found</div>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>First name (as per Aadhaar) <span className="text-red-500">*</span></Label><Input value={signupForm.first_name} onChange={e => setSignupForm({ ...signupForm, first_name: e.target.value })} data-testid="signup-first-name" /></div>
                <div><Label>Last name (as per Aadhaar) <span className="text-red-500">*</span></Label><Input value={signupForm.last_name} onChange={e => setSignupForm({ ...signupForm, last_name: e.target.value })} data-testid="signup-last-name" /></div>
              </div>
              <div><Label>Phone</Label><Input value={signupForm.phone} onChange={e => setSignupForm({ ...signupForm, phone: e.target.value })} data-testid="signup-phone" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Date of birth <span className="text-red-500">*</span></Label><Input type="date" value={signupForm.dob} onChange={e => setSignupForm({ ...signupForm, dob: e.target.value })} data-testid="signup-dob" /><div className="text-[10px] text-[var(--bz-muted)] mt-0.5">Must be 18+</div></div>
                <div><Label>Nominee name <span className="text-red-500">*</span></Label><Input placeholder="Nominee full name" value={signupForm.nominee_name} onChange={e => setSignupForm({ ...signupForm, nominee_name: e.target.value })} data-testid="signup-nominee-name" /></div>
              </div>
              <div><Label>Nominee relationship <span className="text-red-500">*</span></Label><Input placeholder="e.g. Spouse, Parent, Sibling" value={signupForm.nominee_relationship} onChange={e => setSignupForm({ ...signupForm, nominee_relationship: e.target.value })} data-testid="signup-nominee-rel" /></div>
              <div className="rounded-lg border border-[#1D4ED8]/30 p-3 space-y-2">
                <Label className="text-[#1E3A8A] font-semibold">Full address <span className="text-red-500">*</span></Label>
                <Textarea rows={2} placeholder="House no, street, area, landmark" value={signupForm.address} onChange={e => setSignupForm({ ...signupForm, address: e.target.value })} data-testid="signup-address" />
                <div className="grid grid-cols-3 gap-2">
                  <Input placeholder="City" value={signupForm.city} onChange={e => setSignupForm({ ...signupForm, city: e.target.value })} data-testid="signup-city" />
                  <Input placeholder="State" value={signupForm.state} onChange={e => setSignupForm({ ...signupForm, state: e.target.value })} data-testid="signup-state" />
                  <Input placeholder="Pincode" value={signupForm.pincode} onChange={e => setSignupForm({ ...signupForm, pincode: e.target.value })} data-testid="signup-pincode" />
                </div>
              </div>
              <div className="rounded-lg border-2 border-[#1D4ED8]/40 bg-[var(--bz-green-tint)] p-3 space-y-2" data-testid="signup-ekyc-section">
                <div className="flex items-center gap-2">
                  <BadgeCheck className="h-4 w-4 text-[#1E3A8A]" />
                  <Label className="text-[#1E3A8A] font-bold">Complete eKYC <span className="text-red-500">*</span></Label>
                </div>
                <p className="text-[11px] text-[var(--bz-muted)]">Mandatory before payment. Verified by admin — enables the lowest 5% TDS rate on your payouts.</p>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Aadhaar number <span className="text-red-500">*</span></Label><Input value={signupForm.aadhar_number} onChange={e => setSignupForm({ ...signupForm, aadhar_number: e.target.value })} data-testid="signup-aadhar-number" /></div>
                  <div><Label>PAN number <span className="text-red-500">*</span></Label><Input value={signupForm.pan_number} onChange={e => setSignupForm({ ...signupForm, pan_number: e.target.value.toUpperCase() })} data-testid="signup-pan-number" /></div>
                  <div><Label>Bank account <span className="text-red-500">*</span></Label><Input value={signupForm.bank_account} onChange={e => setSignupForm({ ...signupForm, bank_account: e.target.value })} data-testid="signup-bank-account" /></div>
                  <div><Label>IFSC <span className="text-red-500">*</span></Label><Input value={signupForm.ifsc} onChange={e => setSignupForm({ ...signupForm, ifsc: e.target.value.toUpperCase() })} data-testid="signup-ifsc" /></div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div>
                    <Label className="text-xs">Aadhaar front <span className="text-red-500">*</span></Label>
                    <input type="file" accept="image/*" onChange={(e) => uploadFile(e, setSignupForm, 'aadhar_image_url')} className="text-xs" data-testid="signup-aadhar-image" />
                    {signupForm.aadhar_image_url && <img src={signupForm.aadhar_image_url} alt="" className="h-16 mt-1 rounded-sm border" />}
                  </div>
                  <div>
                    <Label className="text-xs">Aadhaar back <span className="text-red-500">*</span></Label>
                    <input type="file" accept="image/*" onChange={(e) => uploadFile(e, setSignupForm, 'aadhar_back_image_url')} className="text-xs" data-testid="signup-aadhar-back-image" />
                    {signupForm.aadhar_back_image_url && <img src={signupForm.aadhar_back_image_url} alt="" className="h-16 mt-1 rounded-sm border" />}
                  </div>
                  <div>
                    <Label className="text-xs">PAN photo <span className="text-red-500">*</span></Label>
                    <input type="file" accept="image/*" onChange={(e) => uploadFile(e, setSignupForm, 'pan_image_url')} className="text-xs" data-testid="signup-pan-image" />
                    {signupForm.pan_image_url && <img src={signupForm.pan_image_url} alt="" className="h-16 mt-1 rounded-sm border" />}
                  </div>
                  <div>
                    <Label className="text-xs">Bank book photo <span className="text-red-500">*</span></Label>
                    <input type="file" accept="image/*" onChange={(e) => uploadFile(e, setSignupForm, 'bankbook_image_url')} className="text-xs" data-testid="signup-bankbook-image" />
                    {signupForm.bankbook_image_url && <img src={signupForm.bankbook_image_url} alt="" className="h-16 mt-1 rounded-sm border" />}
                  </div>
                </div>
              </div>
              <div className="rounded-lg border-2 border-[#1D4ED8]/40 bg-white p-3 space-y-2" data-testid="signup-password-section">
                <div className="flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-[#1E3A8A]" /><Label className="text-[#1E3A8A] font-bold">Create your login password <span className="text-red-500">*</span></Label></div>
                <p className="text-[11px] text-[var(--bz-muted)]">You'll use your Personal ID + this password to sign in. Min 8 characters with at least one letter and one number.</p>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Password <span className="text-red-500">*</span></Label><Input type="password" value={signupForm.password} onChange={e => setSignupForm({ ...signupForm, password: e.target.value })} data-testid="signup-password" /></div>
                  <div><Label>Confirm password <span className="text-red-500">*</span></Label><Input type="password" value={signupForm.confirm_password} onChange={e => setSignupForm({ ...signupForm, confirm_password: e.target.value })} data-testid="signup-confirm-password" /></div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSignupOpen(false)} data-testid="signup-close-btn">Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-screen-2xl mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter display">Network Dashboard</h1>
          <div className="text-sm text-[var(--bz-muted)] mt-1">Personal ID <span className="font-mono-bz font-bold text-[var(--bz-ink)]">{me.node.personal_id}</span> · {me.is_active ? <Badge className="bg-[#1D4ED8]">ACTIVE</Badge> : <Badge variant="destructive">INACTIVE</Badge>}
            {me.rank === 'IR' && (
              <Badge className="ml-2 bg-[var(--bz-orange)] gap-1" data-testid="rank-badge"><Award className="h-3 w-3" />IR · INDIVALIFE Retailer</Badge>
            )}
          </div>
          {me.rank !== 'IR' && me.rank_progress && (
            <div className="mt-2 max-w-xs" data-testid="rank-progress">
              <div className="flex items-center justify-between text-[11px] font-semibold text-[var(--bz-muted)] mb-1">
                <span>Rank progress → IR</span>
                <span className="text-[#1E3A8A]">{me.rank_progress.bv_7d}/{me.rank_progress.target} BV (7 days)</span>
              </div>
              <div className="h-1.5 rounded-full bg-[var(--bz-line)] overflow-hidden">
                <div className="h-full bg-[var(--bz-orange)] transition-all" style={{ width: `${Math.min(100, (me.rank_progress.bv_7d / me.rank_progress.target) * 100)}%` }} />
              </div>
              <div className="text-[10px] text-[var(--bz-muted)] mt-0.5">Buy 100 BV within any 7 days (store or kit) to become an INDIVALIFE Retailer.</div>
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {me.node && (
            <Button onClick={() => setAddOpen(true)} className="bg-[#1D4ED8] hover:bg-[#1E3A8A] gap-2" data-testid="add-downline-btn">
              <Network className="h-4 w-4" />Add downline
            </Button>
          )}
          {(() => {
            const ekycDone = ekycStatus === 'submitted' || ekycStatus === 'approved';
            return (
              <Button
                onClick={ekycDone ? () => setShareOpen(true) : undefined}
                disabled={!ekycDone}
                variant={ekycDone ? 'default' : 'outline'}
                className={`gap-2 ${ekycDone ? 'bg-[var(--bz-orange)] hover:bg-[#e65100] text-white' : 'opacity-60 cursor-not-allowed'}`}
                title={ekycDone ? 'Share a Purchase Kit payment link' : 'Complete your eKYC to unlock the share link'}
                data-testid="share-entry-kit-btn"
              >
                <Link2 className="h-4 w-4" />Share Purchase Kit link
              </Button>
            );
          })()}
          <Button variant="outline" onClick={() => setEkycOpen(true)} className="gap-2" data-testid="open-ekyc-btn"><BadgeCheck className="h-4 w-4" />eKYC</Button>
          <Button variant="outline" onClick={() => setPwOpen(true)} className="gap-2" data-testid="open-set-password-btn"><Wallet className="h-4 w-4" />Set password</Button>
          <Button variant="outline" onClick={() => setRenewOpen(true)} className="gap-2" data-testid="open-renew-btn"><RefreshCw className="h-4 w-4" />Renew (₹{settings.renewal_fee || 708}/28d)</Button>
        </div>
      </div>

      {ekycStatus !== 'approved' && (
        <div
          className={`bz-card p-4 mb-6 border-2 flex items-start gap-3 ${ekycStatus === 'rejected' ? 'border-red-400 bg-red-50' : ekycStatus === 'submitted' ? 'border-blue-300 bg-blue-50' : 'border-amber-400 bg-amber-50'}`}
          data-testid="ekyc-nudge-banner"
        >
          {ekycStatus === 'rejected'
            ? <XCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            : ekycStatus === 'submitted'
              ? <BadgeCheck className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
              : <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />}
          <div className="flex-1">
            <div className={`font-bold ${ekycStatus === 'rejected' ? 'text-red-900' : ekycStatus === 'submitted' ? 'text-blue-900' : 'text-amber-900'}`}>
              {ekycStatus === 'rejected' ? 'Your eKYC was rejected' : ekycStatus === 'submitted' ? 'Your eKYC is under review' : 'Complete your eKYC'}
            </div>
            <div className="text-xs text-[var(--bz-muted)] mt-1">
              {ekycStatus === 'rejected'
                ? 'Please recheck your Aadhaar, PAN and bank details, upload clear photos and resubmit so your account stays verified and withdrawals stay enabled.'
                : ekycStatus === 'submitted'
                  ? 'We\'ve received your details and admin is verifying them. You can update them anytime before approval.'
                  : 'Submit your Aadhaar, PAN and bank details to verify your account and enable withdrawals at the lowest TDS rate.'}
            </div>
            {ekycStatus !== 'submitted' && (
              <Button size="sm" onClick={() => setEkycOpen(true)} className="mt-2 h-8 text-xs bg-[#1D4ED8] hover:bg-[#1E3A8A] gap-1" data-testid="ekyc-nudge-cta">
                <BadgeCheck className="h-3.5 w-3.5" />{ekycStatus === 'rejected' ? 'Fix & resubmit' : 'Complete eKYC now'}
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-4 gap-3 mb-6">
        <Card className="p-4"><div className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)]">Days remaining</div><div className="text-3xl font-black display text-[#1E3A8A]" data-testid="days-remaining">{me.days_remaining}</div></Card>
        <Card className="p-4"><div className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)]">Left BV</div><div className="text-3xl font-black display">{me.node.left_bv || 0}</div></Card>
        <Card className="p-4"><div className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)]">Right BV</div><div className="text-3xl font-black display">{me.node.right_bv || 0}</div></Card>
        <Card className="p-4"><div className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)]">Income (released)</div><div className="text-3xl font-black display text-[#1E3A8A]">₹{me.income.released ?? me.income.pending}</div><div className="text-xs text-[var(--bz-muted)]">Held: ₹{me.income.held ?? 0} · Paid: ₹{me.income.paid}</div></Card>
      </div>

      {/* Two-wallet exposure: Hold (current cycle, auto-releases Sat) + Main (after-TDS, withdrawable) */}
      <WalletsCard />

      {me.days_remaining < 7 && (
        <div className="bz-card p-4 mb-6 border-yellow-500 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600" />
          <div>
            <div className="font-bold">Activation expiring in {me.days_remaining} day(s)</div>
            <div className="text-xs text-[var(--bz-muted)]">Activate by referring at least one member or pay the 28-day soft renewal (₹{settings.renewal_fee || 708}).</div>
          </div>
        </div>
      )}

      {me.volume_eligibility && !me.volume_eligibility.eligible && me.is_active && (
        <div className="bz-card p-4 mb-6 border-2 border-orange-400 bg-orange-50 flex items-start gap-3" data-testid="volume-gating-banner">
          <AlertTriangle className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-bold text-orange-900">Keep your ID active this cycle</div>
            <div className="text-xs text-orange-800 mt-1">
              {me.volume_eligibility.reason === 'no_brand_store_or_deal' && 'You haven\'t opened a brand store or posted any deal yet. Start selling to keep your account eligible.'}
              {me.volume_eligibility.reason === 'no_product_update_this_week' && 'You haven\'t updated/posted a product this week (Sat → Fri 12 PM IST). Update a product to stay eligible.'}
              {me.volume_eligibility.reason === 'no_referral_28d' && 'No referral in the last 28 days. Refer a member to keep your account eligible.'}
              {me.volume_eligibility.reason === 'id_inactive_90d' && 'Your ID is inactive (no self-activation for 90+ days). Renew membership to reactivate.'}
              {me.volume_eligibility.reason === 'no_node' && 'Network node not found — contact admin.'}
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => navigate('/deals')} data-testid="gating-cta-deals">Open Deals</Button>
              <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setRenewOpen(true)} data-testid="gating-cta-renew">Renew now</Button>
            </div>
          </div>
        </div>
      )}

      <Tabs defaultValue="tree">
        <TabsList>
          <TabsTrigger value="tree" data-testid="tab-tree">Genealogy</TabsTrigger>
          <TabsTrigger value="income" data-testid="tab-income">Income ledger</TabsTrigger>
          <TabsTrigger value="statements" data-testid="tab-statements">Weekly statements</TabsTrigger>
        </TabsList>
        <TabsContent value="tree" className="mt-4">
          <Card className="p-4 overflow-x-auto bz-scroll">
            {/* ID search bar + active status check */}
            <div className="mb-4 flex flex-wrap items-end gap-2" data-testid="id-search-row">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs">Check an ID (active / inactive)</Label>
                <div className="flex gap-1">
                  <Input
                    value={searchId}
                    onChange={(e) => setSearchId(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                    placeholder="e.g. IL10042 or INDIVALIFE"
                    data-testid="id-search-input"
                    className="font-mono-bz"
                  />
                  <Button onClick={runSearch} disabled={searchBusy || !searchId.trim()} data-testid="id-search-btn" className="bg-[#1D4ED8] hover:bg-[#1E3A8A]">
                    {searchBusy ? '…' : 'Check'}
                  </Button>
                </div>
              </div>
              {treeStack.length > 0 && (
                <div className="flex items-end gap-1">
                  <Button variant="outline" size="sm" onClick={drillUp} data-testid="tree-drill-up">← Back</Button>
                  <Button variant="outline" size="sm" onClick={drillReset} data-testid="tree-drill-reset">My root</Button>
                </div>
              )}
            </div>

            {searchResult && (
              <div className="mb-4 p-3 rounded-lg border-2 flex items-center justify-between gap-3" style={{ borderColor: searchResult.error ? '#dc2626' : (searchResult.active ? '#10b981' : '#dc2626'), background: searchResult.error ? '#fef2f2' : (searchResult.active ? '#ecfdf5' : '#fef2f2') }} data-testid="id-search-result">
                {searchResult.error ? (
                  <div className="text-sm text-red-700">
                    <span className="font-mono-bz font-bold">{searchResult.personal_id}</span> — not found.
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="font-mono-bz font-bold">{searchResult.personal_id}</div>
                      <div className="text-sm">{searchResult.full_name || searchResult.nickname || ''}{searchResult.town ? ` · ${searchResult.town}` : ''}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full ${searchResult.active ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`} data-testid="id-search-status-pill">
                        <span className={`w-2 h-2 rounded-full ${searchResult.active ? 'bg-blue-500' : 'bg-red-500'}`} />
                        {searchResult.active ? 'Active' : 'Inactive'}
                      </span>
                      <Button size="sm" variant="outline" onClick={() => drillIntoNode(searchResult.personal_id)} data-testid="id-search-drill-btn">View tree →</Button>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="text-xs text-[var(--bz-muted)] mb-3 flex items-center gap-3 flex-wrap">
              <span className="bz-chip bg-blue-50 text-blue-700">● Active</span>
              <span className="bz-chip bg-red-50 text-red-600">● Inactive</span>
              <span className="text-[10px]">💡 Click any downline to view their full genealogy.</span>
              {treeStack.length > 0 && (
                <span className="text-[10px] font-mono-bz">📍 Path: {treeStack.join(' → ')}</span>
              )}
            </div>
            {tree ? <TreeView root={tree} onDrill={drillIntoNode} /> : <div className="text-sm text-[var(--bz-muted)]">Loading tree…</div>}
          </Card>
        </TabsContent>
        <TabsContent value="income">
          <Card className="p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[var(--bz-soft)]"><tr><th className="text-left p-3">Date</th><th className="text-right p-3">Amount</th><th className="text-left p-3">Source</th><th className="text-left p-3">Status</th></tr></thead>
              <tbody>
                {me.income.ledger.length === 0 && <tr><td colSpan={4} className="text-center p-6 text-[var(--bz-muted)]">No income yet</td></tr>}
                {me.income.ledger.map(l => (
                  <tr key={l.ledger_id} className="border-t">
                    <td className="p-3 text-xs text-[var(--bz-muted)]">{new Date(l.at).toLocaleDateString()}</td>
                    <td className="p-3 text-right font-mono-bz font-bold">₹{l.amount}</td>
                    <td className="p-3 text-xs font-mono-bz">{l.source_personal_id || '—'}</td>
                    <td className="p-3"><Badge variant={l.paid ? 'default' : 'outline'} className={l.paid ? 'bg-[#1D4ED8]' : ''}>{l.paid ? 'Paid' : 'Pending'}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
        <TabsContent value="statements">
          <WeeklyStatements />
        </TabsContent>
      </Tabs>

      {/* eKYC */}
      <Dialog open={ekycOpen} onOpenChange={setEkycOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bz-scroll">
          <DialogHeader><DialogTitle>eKYC details</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Aadhaar number</Label><Input value={ekyc.aadhar_number} onChange={e => setEkyc({ ...ekyc, aadhar_number: e.target.value })} /></div>
              <div><Label>PAN</Label><Input value={ekyc.pan_number} onChange={e => setEkyc({ ...ekyc, pan_number: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Bank account</Label><Input value={ekyc.bank_account} onChange={e => setEkyc({ ...ekyc, bank_account: e.target.value })} /></div>
              <div><Label>IFSC</Label><Input value={ekyc.ifsc} onChange={e => setEkyc({ ...ekyc, ifsc: e.target.value })} /></div>
            </div>
            <div><Label>Aadhaar front</Label><input type="file" accept="image/*" onChange={(e) => uploadFile(e, setEkyc, 'aadhar_image_url')} data-testid="ekyc-aadhar-image" />{ekyc.aadhar_image_url && <img src={ekyc.aadhar_image_url} alt="" className="h-20 mt-1 rounded-sm" />}</div>
            <div><Label>Aadhaar back</Label><input type="file" accept="image/*" onChange={(e) => uploadFile(e, setEkyc, 'aadhar_back_image_url')} data-testid="ekyc-aadhar-back-image" />{ekyc.aadhar_back_image_url && <img src={ekyc.aadhar_back_image_url} alt="" className="h-20 mt-1 rounded-sm" />}</div>
            <div><Label>PAN photo</Label><input type="file" accept="image/*" onChange={(e) => uploadFile(e, setEkyc, 'pan_image_url')} />{ekyc.pan_image_url && <img src={ekyc.pan_image_url} alt="" className="h-20 mt-1 rounded-sm" />}</div>
            <div><Label>Bank book photo</Label><input type="file" accept="image/*" onChange={(e) => uploadFile(e, setEkyc, 'bankbook_image_url')} />{ekyc.bankbook_image_url && <img src={ekyc.bankbook_image_url} alt="" className="h-20 mt-1 rounded-sm" />}</div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEkycOpen(false)}>Cancel</Button><Button onClick={submitEKYC} className="bg-[#1D4ED8] hover:bg-[#1E3A8A]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share a Purchase Kit link (pre-selects that exact kit) */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Share a Purchase Kit link</DialogTitle></DialogHeader>
          <p className="text-xs text-[var(--bz-muted)] -mt-2">Pick a kit — the invited person opens the join screen with that exact kit pre-selected and you as their sponsor.</p>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="share-kit-list">
            {kits.length === 0 && <div className="text-sm text-[var(--bz-muted)]">No kits available.</div>}
            {kits.map(k => (
              <div key={k.kit_id} className="flex items-center gap-3 rounded-lg border p-2" data-testid={`share-kit-row-${k.kit_id}`}>
                {k.image_url ? <img src={k.image_url} alt={k.name} className="h-11 w-11 rounded-md object-cover border shrink-0" /> : <div className="h-11 w-11 rounded-md bg-[var(--bz-green-tint)] grid place-items-center shrink-0">📦</div>}
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm text-[#1E3A8A] truncate">{k.name}</div>
                  <div className="text-[11px] text-[var(--bz-muted)]">₹{Math.round(k.total)} · {k.validity_days}d</div>
                </div>
                <Button size="sm" className="bg-[var(--bz-orange)] hover:bg-[#e65100] text-white gap-1" onClick={() => shareEntryKitLink(k.kit_id)} data-testid={`share-kit-btn-${k.kit_id}`}>
                  <Link2 className="h-3.5 w-3.5" />Share
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Renew */}
      <Dialog open={renewOpen} onOpenChange={setRenewOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Renew activation · ₹{renewForm.amount}</DialogTitle></DialogHeader>
          <div className="mb-3">
            <div className="text-[11px] font-semibold text-[var(--bz-muted)] mb-1.5">Choose payment method</div>
            <div className="grid grid-cols-2 gap-2" data-testid="renew-provider-selector">
              {[['razorpay', 'Razorpay'], ['cashfree', 'Cashfree']].map(([val, label]) => (
                <button key={val} type="button" onClick={() => setJoinProvider(val)} data-testid={`renew-provider-${val}`}
                  className={`rounded-lg border-2 p-2 text-sm font-bold transition-all ${joinProvider === val ? 'border-[#1D4ED8] bg-[var(--bz-green-tint)] text-[#1E3A8A]' : 'border-gray-200 bg-white text-[var(--bz-muted)]'}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-lg border-2 border-[#1D4ED8] bg-[var(--bz-green-tint)] p-3 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#1E3A8A] font-bold">Instant renewal</div>
              <div className="text-sm font-semibold">Pay securely with {joinProvider === 'cashfree' ? 'Cashfree' : 'Razorpay'}</div>
            </div>
            <Button
              onClick={() => payWithRazorpay('renewal')}
              disabled={rzpBusy}
              className="bg-[#1D4ED8] hover:bg-[#1E3A8A] gap-2 h-11 px-5 font-bold display"
              data-testid="rzp-pay-renewal-btn"
            >
              {rzpBusy ? 'Opening…' : `Pay ₹${renewForm.amount}`}
            </Button>
          </div>
          <p className="text-[11px] text-[var(--bz-muted)] mt-2">Renewal activates instantly after payment — no admin wait.</p>
          <DialogFooter><Button variant="outline" onClick={() => setRenewOpen(false)} data-testid="renew-close-btn">Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* F5: Set / change password for this user */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Set / change your password</DialogTitle></DialogHeader>
          <p className="text-xs text-[var(--bz-muted)]">Allows you to sign in with your Personal ID + password. Min 8 characters with at least one letter and one digit.</p>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Current password (only if changing)</Label>
              <Input type="password" value={pwForm.current_password} onChange={e => setPwForm({ ...pwForm, current_password: e.target.value })} placeholder="leave blank for first-time setup" data-testid="pw-current-input" />
            </div>
            <div>
              <Label>New password *</Label>
              <Input type="password" value={pwForm.new_password} onChange={e => setPwForm({ ...pwForm, new_password: e.target.value })} data-testid="pw-new-input" />
            </div>
            <div>
              <Label>Confirm new password *</Label>
              <Input type="password" value={pwForm.confirm} onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="pw-confirm-input" />
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPwOpen(false)}>Cancel</Button><Button onClick={submitSetPassword} className="bg-[#1D4ED8] hover:bg-[#1E3A8A]" data-testid="pw-submit-btn">Save password</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add downline member — full eKYC + online Razorpay payment (Entry Kit / 90-day / Yearly) */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bz-scroll">
          <DialogHeader><DialogTitle>Add a downline member</DialogTitle></DialogHeader>
          <div className="bg-[var(--bz-green-tint)] rounded-md p-3 text-xs text-[#1E3A8A]">
            Enter the new member's full eKYC details and pay online. They'll be placed under your ID <span className="font-mono-bz font-bold">{me.node.personal_id}</span> on the <b>{addForm.leg}</b> leg. Membership activates instantly after payment.
          </div>
          <div className="space-y-3">
            {/* Tier selector */}
            <div className="grid grid-cols-3 gap-2" data-testid="add-tier-selector">
              {['entry_kit', 'membership', 'website_yearly'].map(t => {
                const c = tierConfig(t, addForm.kit_id);
                const active = addForm.purpose === t;
                return (
                  <button key={t} type="button" onClick={() => setAddForm({ ...addForm, purpose: t })} data-testid={`add-tier-${t}`}
                    className={`text-left rounded-lg border-2 p-2 transition-all ${active ? 'border-[#1D4ED8] bg-[var(--bz-green-tint)] shadow-md' : 'border-gray-200 bg-white hover:border-[#1D4ED8]/50'}`}>
                    <div className="text-[9px] uppercase tracking-wider font-bold text-[#1E3A8A]">{c.label}</div>
                    <div className="text-base font-black display text-[#1E3A8A] mt-0.5">₹{c.total}</div>
                    <div className="text-[9px] text-[var(--bz-muted)]">{c.days}d · BV {c.bv}</div>
                  </button>
                );
              })}
            </div>
            {addForm.purpose === 'entry_kit' && (
              <div className="rounded-lg bg-[#EAF1FF] p-2.5 space-y-2" data-testid="add-purchase-kit-picker">
                <div className="font-bold text-xs text-[#1E3A8A]">Choose a Purchase Kit</div>
                {kits.map(k => {
                  const active = (addForm.kit_id || kits[0]?.kit_id) === k.kit_id;
                  return (
                    <button key={k.kit_id} type="button" onClick={() => setAddForm({ ...addForm, kit_id: k.kit_id })} data-testid={`add-kit-${k.kit_id}`}
                      className={`w-full flex gap-3 items-center text-left rounded-lg border-2 p-2 transition-all ${active ? 'border-[#1D4ED8] bg-white shadow-sm' : 'border-transparent bg-white/60 hover:border-[#1D4ED8]/40'}`}>
                      {k.image_url
                        ? <img src={k.image_url} alt={k.name} className="h-12 w-12 rounded-md object-cover shrink-0 border" />
                        : <div className="h-12 w-12 rounded-md bg-[#dbe6ff] grid place-items-center shrink-0">📦</div>}
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-[13px] text-[#1E3A8A] truncate">{k.name}</div>
                        <div className="text-[10px] text-[var(--bz-muted)]">{k.validity_days}d · BV {k.bv} · ₹{Math.round(k.total)}</div>
                      </div>
                      {active && <div className="text-[#1D4ED8] font-bold shrink-0">✓</div>}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Leg *</Label>
                <Select value={addForm.leg} onValueChange={(v) => setAddForm({ ...addForm, leg: v })}>
                  <SelectTrigger data-testid="add-leg-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="left">Left</SelectItem><SelectItem value="right">Right</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Parental ID (optional)</Label>
                <Input value={addForm.parental_id} onChange={e => { setAddForm({ ...addForm, parental_id: e.target.value.toUpperCase() }); lookupId(e.target.value, setAddParentalInfo); }} placeholder="e.g. IL10005" data-testid="add-parental-id" />
                {addParentalInfo && !addParentalInfo.error && <div className="text-xs text-[#1E3A8A] mt-1 font-semibold">✓ {addParentalInfo.full_name}</div>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>First name (as per Aadhaar) <span className="text-red-500">*</span></Label><Input value={addForm.first_name} onChange={e => setAddForm({ ...addForm, first_name: e.target.value })} data-testid="add-first-name" /></div>
              <div><Label>Last name (as per Aadhaar) <span className="text-red-500">*</span></Label><Input value={addForm.last_name} onChange={e => setAddForm({ ...addForm, last_name: e.target.value })} data-testid="add-last-name" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Phone <span className="text-red-500">*</span></Label><Input value={addForm.phone} onChange={e => setAddForm({ ...addForm, phone: e.target.value })} data-testid="add-phone" /></div>
              <div><Label>Date of birth <span className="text-red-500">*</span></Label><Input type="date" value={addForm.dob} onChange={e => setAddForm({ ...addForm, dob: e.target.value })} data-testid="add-dob" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Nominee name <span className="text-red-500">*</span></Label><Input value={addForm.nominee_name} onChange={e => setAddForm({ ...addForm, nominee_name: e.target.value })} data-testid="add-nominee-name" /></div>
              <div><Label>Nominee relationship <span className="text-red-500">*</span></Label><Input placeholder="Spouse / Parent…" value={addForm.nominee_relationship} onChange={e => setAddForm({ ...addForm, nominee_relationship: e.target.value })} data-testid="add-nominee-rel" /></div>
            </div>
            <div className="rounded-lg border border-[#1D4ED8]/30 p-3 space-y-2">
              <Label className="text-[#1E3A8A] font-semibold">Full address <span className="text-red-500">*</span></Label>
              <Textarea rows={2} placeholder="House no, street, area, landmark" value={addForm.address} onChange={e => setAddForm({ ...addForm, address: e.target.value })} data-testid="add-address" />
              <div className="grid grid-cols-3 gap-2">
                <Input placeholder="City" value={addForm.city} onChange={e => setAddForm({ ...addForm, city: e.target.value })} data-testid="add-city" />
                <Input placeholder="State" value={addForm.state} onChange={e => setAddForm({ ...addForm, state: e.target.value })} data-testid="add-state" />
                <Input placeholder="Pincode" value={addForm.pincode} onChange={e => setAddForm({ ...addForm, pincode: e.target.value })} data-testid="add-pincode" />
              </div>
            </div>
            <div className="rounded-lg border-2 border-[#1D4ED8]/40 bg-[var(--bz-green-tint)] p-3 space-y-2" data-testid="add-ekyc-section">
              <div className="flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-[#1E3A8A]" /><Label className="text-[#1E3A8A] font-bold">Downline eKYC <span className="text-red-500">*</span></Label></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Aadhaar number <span className="text-red-500">*</span></Label><Input value={addForm.aadhar_number} onChange={e => setAddForm({ ...addForm, aadhar_number: e.target.value })} data-testid="add-aadhar-number" /></div>
                <div><Label>PAN number <span className="text-red-500">*</span></Label><Input value={addForm.pan_number} onChange={e => setAddForm({ ...addForm, pan_number: e.target.value.toUpperCase() })} data-testid="add-pan-number" /></div>
                <div><Label>Bank account <span className="text-red-500">*</span></Label><Input value={addForm.bank_account} onChange={e => setAddForm({ ...addForm, bank_account: e.target.value })} data-testid="add-bank-account" /></div>
                <div><Label>IFSC <span className="text-red-500">*</span></Label><Input value={addForm.ifsc} onChange={e => setAddForm({ ...addForm, ifsc: e.target.value.toUpperCase() })} data-testid="add-ifsc" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div><Label className="text-xs">Aadhaar front <span className="text-red-500">*</span></Label><input type="file" accept="image/*" onChange={(e) => uploadFile(e, setAddForm, 'aadhar_image_url')} className="text-xs" data-testid="add-aadhar-image" />{addForm.aadhar_image_url && <img src={addForm.aadhar_image_url} alt="" className="h-16 mt-1 rounded-sm border" />}</div>
                <div><Label className="text-xs">Aadhaar back <span className="text-red-500">*</span></Label><input type="file" accept="image/*" onChange={(e) => uploadFile(e, setAddForm, 'aadhar_back_image_url')} className="text-xs" data-testid="add-aadhar-back-image" />{addForm.aadhar_back_image_url && <img src={addForm.aadhar_back_image_url} alt="" className="h-16 mt-1 rounded-sm border" />}</div>
                <div><Label className="text-xs">PAN photo <span className="text-red-500">*</span></Label><input type="file" accept="image/*" onChange={(e) => uploadFile(e, setAddForm, 'pan_image_url')} className="text-xs" data-testid="add-pan-image" />{addForm.pan_image_url && <img src={addForm.pan_image_url} alt="" className="h-16 mt-1 rounded-sm border" />}</div>
                <div><Label className="text-xs">Bank book photo <span className="text-red-500">*</span></Label><input type="file" accept="image/*" onChange={(e) => uploadFile(e, setAddForm, 'bankbook_image_url')} className="text-xs" data-testid="add-bankbook-image" />{addForm.bankbook_image_url && <img src={addForm.bankbook_image_url} alt="" className="h-16 mt-1 rounded-sm border" />}</div>
              </div>
            </div>
            <div className="rounded-lg border-2 border-[#1D4ED8]/40 bg-white p-3 space-y-2" data-testid="add-password-section">
              <div className="flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-[#1E3A8A]" /><Label className="text-[#1E3A8A] font-bold">Set member's login password <span className="text-red-500">*</span></Label></div>
              <p className="text-[11px] text-[var(--bz-muted)]">Share this with the new member so they can sign in with their Personal ID. Min 8 chars, a letter and a number.</p>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Password <span className="text-red-500">*</span></Label><Input type="text" value={addForm.password} onChange={e => setAddForm({ ...addForm, password: e.target.value })} data-testid="add-password" /></div>
                <div><Label>Confirm <span className="text-red-500">*</span></Label><Input type="text" value={addForm.confirm_password} onChange={e => setAddForm({ ...addForm, confirm_password: e.target.value })} data-testid="add-confirm-password" /></div>
              </div>
            </div>
            <div className="rounded-lg border border-[#1D4ED8]/30 p-3 space-y-2">
              <Label className="text-[#1E3A8A] font-semibold">Payment method</Label>
              <div className="grid grid-cols-2 gap-2" data-testid="dl-provider-selector">
                {[['razorpay', 'Razorpay'], ['cashfree', 'Cashfree']].map(([val, label]) => (
                  <button key={val} type="button" onClick={() => setDlProvider(val)} data-testid={`dl-provider-${val}`}
                    className={`rounded-lg border-2 p-2 text-sm font-bold transition-all ${dlProvider === val ? 'border-[#1D4ED8] bg-[var(--bz-green-tint)] text-[#1E3A8A]' : 'border-gray-200 bg-white text-[var(--bz-muted)]'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={payForDownline} disabled={addBusy} className="bg-[#1D4ED8] hover:bg-[#1E3A8A] font-bold" data-testid="submit-add-downline">{addBusy ? 'Opening…' : `Pay ₹${tierConfig(addForm.purpose, addForm.kit_id).total} with ${dlProvider === 'cashfree' ? 'Cashfree' : 'Razorpay'}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Downline credentials — sponsor shares ID + password with the new member on the spot */}
      <Dialog open={!!dlCreds} onOpenChange={(o) => { if (!o) setDlCreds(null); }}>
        <DialogContent className="max-w-md" data-testid="downline-creds-dialog">
          <DialogHeader><DialogTitle>Downline added 🎉</DialogTitle></DialogHeader>
          {dlCreds && (
            <div className="space-y-3">
              <p className="text-sm text-[var(--bz-muted)]">Share these login details with <b>{dlCreds.name || 'the new member'}</b> now. They sign in with <b>ID + Password</b>.</p>
              <div className="rounded-xl border-2 border-[#1D4ED8]/40 bg-[var(--bz-green-tint)] p-4 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] uppercase tracking-wider font-bold text-[var(--bz-muted)]">Personal ID</span>
                  <span className="font-mono-bz font-black text-[#1E3A8A]" data-testid="creds-id">{dlCreds.personal_id}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] uppercase tracking-wider font-bold text-[var(--bz-muted)]">Password</span>
                  <span className="font-mono-bz font-black text-[#1E3A8A]" data-testid="creds-password">{dlCreds.password}</span>
                </div>
              </div>
              <Button
                className="w-full bg-[#1D4ED8] hover:bg-[#1E3A8A] font-bold gap-2"
                data-testid="creds-copy-btn"
                onClick={() => { navigator.clipboard?.writeText(`INDIVALIFE login\nID: ${dlCreds.personal_id}\nPassword: ${dlCreds.password}`); toast.success('Login details copied'); }}
              >Copy login details</Button>
              <p className="text-[11px] text-amber-700">For security, this password won't be shown again — copy or note it now.</p>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDlCreds(null)} data-testid="creds-close-btn">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NetworkPage;
